import React, { useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Settings } from "lucide-react";
import KPITrendChart from "../KPITrendChart/KPITrendChart";
import {
  KPIIncompleteReason,
  KPIUnavailableReason,
} from "../../api/fetchMemberDashboard/types";
import { DashboardKPI } from "../../types";
import permissionsService from "@/services/permissionsService";
import "./KPICard.css";

interface KPICardProps {
  kpi: DashboardKPI;
}

/** Kicker on the back face: CertainPath red on white, amber on slate. */
const BACK_EYEBROW =
  "mb-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-primary dark:text-amber-400";

/** Matches the page's kicker treatment. See MemberDashboardPage. */
const EYEBROW =
  "text-xs font-semibold uppercase tracking-[0.12em] text-amber-600 dark:text-amber-400 mb-1.5";

/**
 * What "incomplete" means, per cause — and per whether there is a figure for it
 * to be incomplete *about*.
 *
 * One sentence for every cause is how a ServiceTitan card ends up telling a
 * member their QuickBooks accounts are unmapped, so the copy is keyed on the
 * reason the API sends. It is keyed on the figure as well because "this figure
 * is incomplete" is false when the card is showing a dash: a member who has
 * mapped nothing has no figure at all, and calling that incomplete describes a
 * number they cannot see.
 */
const INCOMPLETE_COPY: Record<
  KPIIncompleteReason,
  { withFigure: string; withoutFigure: string }
> = {
  unmapped_accounts: {
    withFigure:
      "Some QuickBooks accounts aren't mapped and confirmed yet, so this figure is incomplete.",
    withoutFigure:
      "This card can't be calculated until your QuickBooks accounts are mapped and confirmed.",
  },
  business_units_without_work_type: {
    withFigure:
      "Some of your ServiceTitan business units have no work type set yet, so their jobs aren't counted here.",
    withoutFigure:
      "This card can't be calculated until your ServiceTitan business units have a work type set.",
  },
  unreadable_job_dates: {
    withFigure:
      "Some jobs arrived from ServiceTitan without a completion date we could read, so they aren't counted in any month.",
    withoutFigure:
      "No job in this period arrived from ServiceTitan with a completion date we could read, so there is nothing to plot.",
  },
};

/**
 * Whether the card is showing the member a number anywhere — the headline, or
 * any month of the chart.
 *
 * A headline month that came out null while earlier months have figures is
 * still a card with something on it, so the chart is asked too rather than the
 * headline alone.
 */
const hasFigure = (kpi: DashboardKPI): boolean =>
  kpi.valueLabel !== null || kpi.chart.some((point) => point.value !== null);

/**
 * Names the accounts a card is missing, as the QuickBooks mapping screen names
 * them — that is where the member goes to look for them.
 */
const formatAccountList = (labels: string[]): string => {
  if (labels.length <= 1) {
    return labels[0] ?? "an account this card needs";
  }

  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
};

/**
 * The kicker over an unavailable card.
 *
 * Four of the five reasons state something about the member that will still be
 * true on the next request, and "Not available" is the right register for
 * those. An outage is ours and clears itself, so it gets a heading that says
 * come back rather than one that reads as a verdict.
 */
const unavailableEyebrow = (kpi: DashboardKPI): string => {
  if (kpi.unavailableReason === "servicetitan_unreachable") {
    return "Temporarily unavailable";
  }

  // An empty card the backend still calls available is one the member has not
  // finished setting up — not a verdict on their books, which is what "Not
  // available" reads as next to a card that has a number.
  return kpi.isAvailable ? "Not available yet" : "Not available";
};

/**
 * Why this card has no figure, in the member's words.
 *
 * Keyed on the reason and never on "unavailable" alone: every reason lands in
 * the same block, and the wrong sentence here tells a member something false
 * about their own books. The fallback is the missing-account case, which is
 * the only one that names data the member controls in QuickBooks — so a new
 * reason added to the API without a branch here must never reach it silently,
 * which is what the switch is for.
 *
 * An empty card with no unavailable reason at all is the member who has mapped
 * nothing: the backend calls it available and incomplete, because availability
 * is asked only once the mapping is finished. The incomplete reason carries the
 * sentence in that case.
 */
const emptyCardCopy = (kpi: DashboardKPI): React.ReactNode => {
  if (kpi.unavailableReason === null) {
    return INCOMPLETE_COPY[kpi.incompleteReason ?? "unmapped_accounts"]
      .withoutFigure;
  }

  switch (kpi.unavailableReason) {
    /*
      Nothing is unmapped here, so the sentence must not mention mapping. This
      member's books resolved perfectly and their equity is under water —
      telling them to go fix a mapping would send them looking for a problem
      that does not exist.
    */
    case "non_positive_equity":
      return (
        <>
          This ratio divides by your equity, and yours has not been above zero
          in this period. There is no ratio to plot until it is.
        </>
      );

    /*
      Their ServiceTitan configuration, not their performance. Without a
      bookable reason there is no denominator, and plotting 100% because every
      call we found happened to book would be worse than plotting nothing.
    */
    case "no_bookable_call_reasons":
      return (
        <>
          None of your ServiceTitan call reasons is marked bookable yet, so
          there is nothing to measure against. Mark the ones a customer could
          book from.
        </>
      );

    /*
      A ServiceTitan card, so no mention of QuickBooks at all. The member has
      business units; none is marked as the kind of work this card measures,
      which is a setting they can change.
    */
    case "no_work_type_assigned":
      return (
        <>
          None of your ServiceTitan business units is set to{" "}
          {kpi.workTypeLabel ?? "this kind of"} work yet. Set one to see this
          card.
        </>
      );

    /*
      The only reason that is about us rather than about them, and the only one
      that will be gone by the next request. So it says three things the other
      four must not: whose fault it is, that their setup is fine, and that
      coming back is the fix. The card is returned at all — rather than the
      section quietly shrinking from fifteen cards to ten — because a section
      that disappears reads as "you have no ServiceTitan".
    */
    case "servicetitan_unreachable":
      return (
        <>
          We couldn&apos;t reach ServiceTitan just now, so this card has no
          figures for the period. Nothing is wrong with your setup — reload the
          page in a few minutes and it should be back.
        </>
      );

    default:
      return (
        <>
          {/*
            "Field Labor" stays bare, with no "account" after it: it is the
            exact option the member picks under the mapping screen's
            "CertainPath account" column, and repeating the word would put
            "account" in one sentence twice with two different referents —
            theirs and ours.
          */}
          No QuickBooks account is mapped to{" "}
          {formatAccountList(kpi.missingAccountLabels)} yet. Please map one to
          see this card.
        </>
      );
  }
};

/**
 * Where the member goes to fix this card, or null when nothing on the card is
 * theirs to configure.
 *
 * The two nulls are the reasons that name no setting: an outage is ours and
 * clears itself, and equity under water is a fact about their books. Sending
 * either one to an integrations screen would promise a switch that is not
 * there. Every other reason names something behind one of the two screens —
 * the QuickBooks page carries the account mapping, the ServiceTitan page the
 * business units.
 */
const settingsLink = (
  reason: KPIUnavailableReason | null,
): { href: string; label: string } | null => {
  switch (reason) {
    case "servicetitan_unreachable":
    case "non_positive_equity":
      return null;

    case "no_bookable_call_reasons":
    case "no_work_type_assigned":
      return {
        href: "/hub/settings/integrations/servicetitan",
        label: "Configure ServiceTitan",
      };

    default:
      return {
        href: "/hub/settings/integrations/quickbooks",
        label: "Configure QuickBooks",
      };
  }
};

/**
 * The same, for the incomplete state — which is the one a member with nothing
 * mapped actually lands on.
 *
 * `isAvailable` is asked only once the mapping is finished, so a member who has
 * mapped nothing at all never reaches the unavailable block and never sees its
 * link. That leaves the state most in need of a route to the mapping screen as
 * the only one without one, which is why this exists separately rather than
 * both branches sharing `settingsLink`.
 *
 * Unreadable job dates get nothing: no screen the member can open will fix a
 * timestamp that arrived unparseable.
 */
const incompleteSettingsLink = (
  reason: KPIIncompleteReason | null,
): { href: string; label: string } | null => {
  switch (reason) {
    case "unreadable_job_dates":
      return null;

    case "business_units_without_work_type":
      return {
        href: "/hub/settings/integrations/servicetitan",
        label: "Set business unit work types",
      };

    default:
      return {
        href: "/hub/settings/integrations/quickbooks",
        label: "Map your chart of accounts",
      };
  }
};

/**
 * The screen that fixes an empty card, whichever check left it empty — the
 * unavailable reason when there is one, the incomplete reason when the member
 * simply has not started mapping.
 */
const emptyCardSettingsLink = (
  kpi: DashboardKPI,
): { href: string; label: string } | null =>
  kpi.unavailableReason === null
    ? incompleteSettingsLink(kpi.incompleteReason)
    : settingsLink(kpi.unavailableReason);

/**
 * The card is a button that flips, so both handlers stop here. Enter on the
 * link would otherwise reach the card's own keydown, which calls
 * preventDefault — flipping the card instead of following the link.
 */
const SettingsLink: React.FC<{
  className: string;
  href: string;
  label: string;
}> = ({ className, href, label }) => (
  <Link
    className={`inline-flex items-center gap-1.5 text-xs font-semibold hover:underline ${className}`}
    onClick={(event) => event.stopPropagation()}
    onKeyDown={(event) => event.stopPropagation()}
    to={href}
  >
    <Settings className="h-3.5 w-3.5" />
    {label}
  </Link>
);

const KPICard: React.FC<KPICardProps> = ({ kpi }) => {
  const [flipped, setFlipped] = useState(false);
  const { hasPermission } = permissionsService();

  /*
    Both screens sit behind `integrations`, which the dashboard's own
    `member-dashboard` permission does not imply — a coach has the dashboard
    and not the settings. Offering them a cogwheel would send them into a
    PermissionGuard.
  */
  const canReachSettings = hasPermission("integrations");

  /*
    Whether the card has anything to show, which is what decides the treatment
    — not `isAvailable`, which answers the narrower question of whether the
    backend could compute the card once the mapping was finished. The two came
    apart for the member who has mapped nothing: available, incomplete, and
    rendering a dash over an empty chart with guidance about a trend that does
    not exist. An empty card gets the empty-card treatment however it got empty.
  */
  const showsFigure = kpi.isAvailable && hasFigure(kpi);

  const emptyCardLink = canReachSettings ? emptyCardSettingsLink(kpi) : null;

  const incompleteLink = canReachSettings
    ? incompleteSettingsLink(kpi.incompleteReason)
    : null;

  const toggle = () => setFlipped((current) => !current);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggle();
    }
  };

  return (
    <div
      aria-label={`${kpi.name}. ${flipped ? "Showing how it is calculated" : "Showing the trend"}. Activate to flip.`}
      aria-pressed={flipped}
      className={`kpi-card cursor-pointer rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 dark:focus-visible:ring-slate-500 ${
        flipped ? "is-flipped" : ""
      }`}
      onClick={toggle}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className="relative">
        <div className="kpi-card__face kpi-card__front">
          <div className="flex min-h-[32rem] flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-start justify-between gap-3">
              <span className="text-xs font-medium uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400">
                {kpi.source}
              </span>
            </div>

            <div>
              <h3 className="text-xl font-bold tracking-tight text-secondary dark:text-white">
                {kpi.name}
              </h3>
              {/*
                A card with nothing to show shows no headline at all rather than
                a dash. A dash reads as "we looked and found nothing"; this card
                has not looked, because there is nothing yet to look in.
              */}
              {showsFigure && (
                <>
                  <div className="mt-2 flex flex-wrap items-baseline gap-3">
                    <span className="text-5xl font-bold leading-none tracking-tight text-secondary dark:text-white">
                      {kpi.valueLabel ?? "—"}
                    </span>
                    {kpi.deltaLabel && (
                      <span
                        className={`text-sm ${
                          kpi.deltaIsGood
                            ? "text-emerald-700 dark:text-emerald-400"
                            : "text-red-700 dark:text-red-400"
                        }`}
                      >
                        {kpi.deltaIsUp ? "↑" : "↓"} {kpi.deltaLabel}
                      </span>
                    )}
                  </div>
                  {kpi.monthLabel && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {kpi.monthLabel}
                    </p>
                  )}
                </>
              )}
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300">
              {kpi.definition}
            </p>

            {showsFigure ? (
              <>
                <KPITrendChart series={kpi.chart} />

                {kpi.isIncomplete && (
                  <div
                    className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-500/10 dark:text-amber-200"
                    data-testid="kpi-incomplete"
                  >
                    <p>
                      {
                        INCOMPLETE_COPY[
                          kpi.incompleteReason ?? "unmapped_accounts"
                        ].withFigure
                      }
                    </p>
                    {incompleteLink && (
                      <SettingsLink
                        className="mt-2 text-amber-900 dark:text-amber-200"
                        href={incompleteLink.href}
                        label={incompleteLink.label}
                      />
                    )}
                  </div>
                )}

                <div className="border-t border-gray-200 pt-4 dark:border-slate-700">
                  <div className={EYEBROW}>What to look for</div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {kpi.guidance}
                  </p>
                </div>
              </>
            ) : (
              /*
                Not an error, and not a warning — nothing here is going wrong.
                Neutral treatment, and the guidance is dropped along with the
                chart: both describe a trend that does not exist.

                The copy describes the *mapping*, never the business. Nothing
                here can tell "you don't track field labor" apart from "you
                mapped your field labor account to Other Direct Cost", and the
                second is the likelier of the two — so it says what is missing
                and how to fix it rather than telling a member what their own
                books contain.
              */
              <div
                className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/50"
                data-testid="kpi-unavailable"
              >
                <div className={EYEBROW}>{unavailableEyebrow(kpi)}</div>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {emptyCardCopy(kpi)}
                </p>
                {emptyCardLink && (
                  <SettingsLink
                    className="mt-2.5 text-secondary dark:text-white"
                    href={emptyCardLink.href}
                    label={emptyCardLink.label}
                  />
                )}
              </div>
            )}

            <div className="mt-auto flex items-center gap-2 pt-2 text-xs font-semibold text-amber-700 dark:text-amber-400">
              <RefreshCw className="h-4 w-4" />
              Tap card for the formula
            </div>
          </div>
        </div>

        <div className="kpi-card__face kpi-card__back">
          {/*
            Two palettes, one structure. Light mode is a white card with
            CertainPath red as the accent; dark mode borrows the nav shell's
            slate-900 with amber, so the back reads as part of the app.

            The accent has to change with the surface — amber on white is
            roughly 1.7:1 against text, and red on slate-900 is little better.
            Each palette picks the brand colour that survives its background.
          */}
          <div className="flex h-full flex-col gap-5 overflow-auto rounded-lg border border-gray-200 bg-white p-6 dark:border-transparent dark:bg-slate-900">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className={BACK_EYEBROW}>How we calculate it</div>
                <h3 className="text-xl font-bold tracking-tight text-secondary dark:text-white">
                  {kpi.name}
                </h3>
              </div>
              <span className="flex-none rounded-full border border-secondary/25 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.07em] text-secondary/70 dark:border-white/20 dark:text-white/60">
                {kpi.source}
              </span>
            </div>

            {/*
              CertainPath red in both themes, so the formula reads the same way
              on a white card as on a slate one. Everything on it is white —
              nothing else clears contrast against this red at body size.
            */}
            <div className="flex items-center justify-center gap-4 rounded-xl bg-primary px-5 py-6">
              {/*
                A KPI with no denominator is a single figure, not a ratio — a
                balance sheet card has nothing to divide by. The rule and the
                bottom line are dropped rather than left empty, so the block
                reads as a statement instead of a fraction missing its half.
              */}
              <div className="flex max-w-xs flex-col items-center text-center">
                <span
                  className={`text-sm font-semibold leading-snug text-white ${kpi.formula.denominator ? "pb-2.5" : ""}`}
                >
                  {kpi.formula.numerator}
                </span>
                {kpi.formula.denominator && (
                  <>
                    <span className="w-full border-t-2 border-white" />
                    <span className="pt-2.5 text-sm font-semibold leading-snug text-white">
                      {kpi.formula.denominator}
                    </span>
                  </>
                )}
              </div>
              {kpi.formula.multiplier && (
                <span className="whitespace-nowrap text-xl font-bold text-white">
                  {kpi.formula.multiplier}
                </span>
              )}
            </div>

            {kpi.formula.workedExample && (
              <div className="rounded-r-lg border-l-[3px] border-primary bg-primary/5 px-4 py-3 dark:border-amber-400 dark:bg-amber-400/10">
                <div className={BACK_EYEBROW}>
                  Your numbers for {kpi.monthLabel}
                </div>
                <p className="text-lg font-semibold text-secondary dark:text-white">
                  {kpi.formula.workedExample}
                </p>
              </div>
            )}

            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-white/45">
                What goes into it
              </div>
              <p className="text-sm leading-relaxed text-gray-600 dark:text-white/80">
                {kpi.formula.note}
              </p>
            </div>

            <div className="mt-auto flex items-center gap-2 border-t border-gray-200 pt-4 text-xs font-semibold text-primary dark:border-white/10 dark:text-amber-400">
              <RefreshCw className="h-4 w-4" />
              Tap card to flip back
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPICard;
